import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { payrollKeys } from './queryKeys';
import type { EmployeePayrollData } from '@/services/payroll/types';

async function fetchPayrollEmployees(
  startDate: string,
  endDate: string
): Promise<EmployeePayrollData[]> {
  // Get current user's company
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: profile } = await supabase
    .from('profiles')
    .select('company_id')
    .eq('user_id', user.id)
    .single();

  if (!profile) throw new Error('Profile not found');

  // Fetch all active employees with their time entry stats
  const { data: employees, error } = await supabase
    .from('profiles')
    .select(`
      id,
      first_name,
      last_name,
      email,
      role,
      avatar_url
    `)
    .eq('company_id', profile.company_id)
    .eq('status', 'active')
    .order('first_name');

  if (error) throw error;
  if (!employees || employees.length === 0) return [];

  const employeeIds = employees.map(e => e.id);

  // Single batch query for all employees' time entries in the date range
  const { data: allTimeEntries } = await supabase
    .from('time_entries')
    .select('profile_id, clock_in, clock_out, status')
    .in('profile_id', employeeIds)
    .gte('clock_in', startDate)
    .lte('clock_in', endDate)
    .limit(10000);

  // Group time entries by profile_id
  const entriesByProfile = new Map<string, typeof allTimeEntries>();
  for (const entry of allTimeEntries || []) {
    const existing = entriesByProfile.get(entry.profile_id) || [];
    existing.push(entry);
    entriesByProfile.set(entry.profile_id, existing);
  }

  // Build employee payroll data from grouped entries
  const employeeData: EmployeePayrollData[] = [];

  for (const employee of employees) {
    const entries = entriesByProfile.get(employee.id) || [];
    const approvedEntries = entries.filter(te => te.status === 'approved' && te.clock_out);
    if (approvedEntries.length === 0) continue;

    const total_hours = approvedEntries.reduce((sum, te) => {
      const hours = (new Date(te.clock_out!).getTime() - new Date(te.clock_in).getTime()) / (1000 * 60 * 60);
      return sum + hours;
    }, 0);

    employeeData.push({
      id: employee.id,
      profile_id: employee.id,
      first_name: employee.first_name || '',
      last_name: employee.last_name || '',
      email: employee.email,
      role: employee.role || 'STAFF',
      avatar_url: employee.avatar_url,
      total_hours,
      regular_hours: Math.min(total_hours, 40),
      overtime_hours: Math.max(total_hours - 40, 0),
      holiday_hours: 0,
      has_approved_entries: true,
    });
  }

  return employeeData;
}

export const usePayrollEmployees = (startDate: string, endDate: string) => {
  return useQuery({
    queryKey: payrollKeys.employees(startDate, endDate),
    queryFn: () => fetchPayrollEmployees(startDate, endDate),
    enabled: !!startDate && !!endDate,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};
